/**
 * Shared, dependency-free renderer for the small markdown-ish dialect
 * used by TERMS_BODY / PRIVACY_BODY / the Pro+ Billing Terms doc.
 *
 * Recognises: `# H1`, `## H2`, `---` hr, `- ` bullet lists, `**bold**`,
 * `_italic_`, `[label](url)` links, and plain paragraphs. Unlike a
 * naive line-by-line renderer, this correctly joins hard-wrapped
 * continuation lines back into the same paragraph/bullet (the source
 * docs wrap long lines for readability, they aren't separate blocks).
 *
 * Used by the standalone `/terms`, `/privacy`, and
 * `/terms/pro-plus-billing` pages so all three share one visual style.
 */
import React from "react";

function parseBlocks(text) {
    const lines = text.split("\n");
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i].trim();
        if (line === "") { i++; continue; }
        if (line === "---") { blocks.push({ type: "hr" }); i++; continue; }
        if (line.startsWith("## ")) { blocks.push({ type: "h2", text: line.slice(3) }); i++; continue; }
        if (line.startsWith("# ")) { blocks.push({ type: "h1", text: line.slice(2) }); i++; continue; }
        if (line.startsWith("- ")) {
            const items = [line.slice(2)];
            i++;
            while (i < lines.length) {
                const l = lines[i].trim();
                if (l === "" || l.startsWith("#") || l === "---") break;
                if (l.startsWith("- ")) { items.push(l.slice(2)); }
                else { items[items.length - 1] += " " + l; }
                i++;
            }
            blocks.push({ type: "ul", items });
            continue;
        }
        const paraLines = [line];
        i++;
        while (i < lines.length) {
            const l = lines[i].trim();
            if (l === "" || l.startsWith("#") || l === "---" || l.startsWith("- ")) break;
            paraLines.push(l);
            i++;
        }
        blocks.push({ type: "p", text: paraLines.join(" ") });
    }
    return blocks;
}

function renderInline(text) {
    const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
    const boldRe = /\*\*([^*]+)\*\*/g;
    const italicRe = /_([^_]+)_/g;
    let out = text;
    const linkTokens = [];
    out = out.replace(linkRe, (m, label, href) => {
        const t = `\u0000LNK${linkTokens.length}\u0000`;
        linkTokens.push({ label, href });
        return t;
    });
    const boldTokens = [];
    out = out.replace(boldRe, (m, txt) => {
        const t = `\u0000B${boldTokens.length}\u0000`;
        boldTokens.push(txt);
        return t;
    });
    const italicTokens = [];
    out = out.replace(italicRe, (m, txt) => {
        const t = `\u0000I${italicTokens.length}\u0000`;
        italicTokens.push(txt);
        return t;
    });
    const segments = out.split(/(\u0000(?:LNK|B|I)\d+\u0000)/);
    return segments.map((seg, i) => {
        const lnk = seg.match(/^\u0000LNK(\d+)\u0000$/);
        if (lnk) {
            const { label, href } = linkTokens[+lnk[1]];
            const external = /^https?:\/\//.test(href);
            return (
                <a key={i} href={href}
                   {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                   className="text-cyber underline hover:text-cyber/80">
                    {label}
                </a>
            );
        }
        const b = seg.match(/^\u0000B(\d+)\u0000$/);
        if (b) return <strong key={i} className="text-white font-black">{boldTokens[+b[1]]}</strong>;
        const it = seg.match(/^\u0000I(\d+)\u0000$/);
        if (it) return <em key={i} className="text-white/60">{italicTokens[+it[1]]}</em>;
        return seg;
    });
}

export function renderMarkdownBody(md) {
    return parseBlocks(md).map((b, i) => {
        if (b.type === "h1") {
            return (
                <h1 key={i} className="text-2xl md:text-3xl font-black text-white tracking-tight mt-2 mb-3">
                    {renderInline(b.text)}
                </h1>
            );
        }
        if (b.type === "h2") {
            return (
                <h2 key={i} className="text-lg font-black text-cyber uppercase tracking-wide mt-6 mb-2">
                    {renderInline(b.text)}
                </h2>
            );
        }
        if (b.type === "hr") return <hr key={i} className="border-white/10 my-6" />;
        if (b.type === "ul") {
            return (
                <ul key={i} className="list-disc list-outside ml-5 space-y-1.5 text-white/80 text-sm leading-relaxed">
                    {b.items.map((item, j) => <li key={j}>{renderInline(item)}</li>)}
                </ul>
            );
        }
        return (
            <p key={i} className="text-sm text-white/80 leading-relaxed">
                {renderInline(b.text)}
            </p>
        );
    });
}
